import React, { useState, useEffect } from 'react';
import { Calendar, RefreshCw, ShoppingBag, Zap, TrendingUp, Rocket, Download, X, CreditCard, Eye } from 'lucide-react';
import { SoArtisticCoinPackage, AdAccount, AdInvoice } from '../types/ads'; // Adjust path as needed
import { adsApiService } from '../services/adsApiService'; // Import adsApiService
import { loadStripe } from '@stripe/stripe-js';
import { EmbeddedCheckout, EmbeddedCheckoutProvider } from '@stripe/react-stripe-js';

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY as string);

const tokenPackages: SoArtisticCoinPackage[] = [
  { id: 'basic', name: 'Paquete Básico', quantity: 100, bonus: 0, totalTokens: 100, priceEUR: 10.0 },
  { id: 'boost', name: 'Paquete Impulso', quantity: 500, bonus: 5, totalTokens: 525, priceEUR: 50.0 },
  { id: 'creator', name: 'Paquete Creador', quantity: 1000, bonus: 10, totalTokens: 1100, priceEUR: 100.0 },
  { id: 'agency', name: 'Paquete Agencia', quantity: 5000, bonus: 15, totalTokens: 5750, priceEUR: 500.0 },
];

const SAC_PER_30S_BLOCK = 9;
const MAX_BLOCKS_PER_24H_PER_USER = 240;
const MAX_CAMPAIGNS_PER_USER = 100;

const PurchaseDetailModal: React.FC<{ invoice: AdInvoice; onClose: () => void; formatCurrency: (amount: number) => string; formatSAC: (amount: number) => string }> = ({ invoice, onClose, formatCurrency, formatSAC }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl max-w-md w-full">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-gray-900">Detalles de la Compra</h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full"
            >
              <X size={20} />
            </button>
          </div>

          <div className="space-y-4 text-gray-700">
            <div>
              <p className="text-sm font-medium text-gray-600">Número de Factura:</p>
              <p className="text-base font-semibold text-gray-900">{invoice.invoice_number}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600">Fecha de Compra:</p>
              <p className="text-base font-semibold text-gray-900">{new Date(invoice.created_at).toLocaleDateString('es-ES')}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600">Período de Facturación:</p>
              <p className="text-base font-semibold text-gray-900">
                {new Date(invoice.billing_period_start).toLocaleDateString('es-ES')} - {new Date(invoice.billing_period_end).toLocaleDateString('es-ES')}
              </p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600">Tokens Comprados (SAC):</p>
              <p className="text-base font-semibold text-gray-900">{formatSAC(invoice.total_spend)}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600">Impuestos:</p>
              <p className="text-base font-semibold text-gray-900">{formatCurrency(invoice.tax_amount)}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600">Monto Total Pagado:</p>
              <p className="text-base font-semibold text-gray-900">{formatCurrency(invoice.total_amount)}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600">Estado:</p>
              <p className="text-base font-semibold text-gray-900">
                <span className={`inline-block px-2 py-1 text-xs rounded-full font-medium ${invoice.status === 'paid' ? 'bg-green-100 text-green-800' : invoice.status === 'pending' ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'}`}>
                  {invoice.status === 'paid' ? 'Completado' : invoice.status === 'pending' ? 'Pendiente' : 'Fallido'}
                </span>
              </p>
            </div>
            {invoice.due_date && (
              <div>
                <p className="text-sm font-medium text-gray-600">Fecha de Vencimiento:</p>
                <p className="text-base font-semibold text-gray-900">{new Date(invoice.due_date).toLocaleDateString('es-ES')}</p>
              </div>
            )}
            {invoice.paid_at && (
              <div>
                <p className="text-sm font-medium text-gray-600">Pagado el:</p>
                <p className="text-base font-semibold text-gray-900">{new Date(invoice.paid_at).toLocaleDateString('es-ES')}</p>
              </div>
            )}
            {invoice.payment_method && (
              <div>
                <p className="text-sm font-medium text-gray-600">Método de Pago:</p>
                <p className="text-base font-semibold text-gray-900">{invoice.payment_method}</p>
              </div>
            )}
          </div>

          <div className="pt-6">
            <button
              onClick={onClose}
              className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
            >
              Cerrar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const BillingView: React.FC = () => {
  const [purchasingPackageId, setPurchasingPackageId] = useState<string | null>(null);
  const [purchaseMessage, setPurchaseMessage] = useState<string | null>(null);
  const [purchaseError, setPurchaseError] = useState<string | null>(null);
  const [showCheckoutModal, setShowCheckoutModal] = useState(false);
  const [checkoutClientSecret, setCheckoutClientSecret] = useState<string | null>(null);
  const [invoices, setInvoices] = useState<AdInvoice[]>([]);
  const [selectedInvoice, setSelectedInvoice] = useState<AdInvoice | null>(null);
  const [showPurchaseDetailModal, setShowPurchaseDetailModal] = useState(false);
  const [adAccount, setAdAccount] = useState<AdAccount | null>(null);

  const loadData = async () => {
    try {
      const accounts = await adsApiService.getAdAccounts();
      if (accounts.length > 0) {
        setAdAccount(accounts[0]);
      }
      const fetchedInvoices = await adsApiService.getInvoices();
      setInvoices(fetchedInvoices);
    } catch (error) {
      console.error('Error loading billing data:', error);
    }
  };

  useEffect(() => {
    void loadData();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const checkout = params.get('checkout');
    if (checkout === 'success') {
      setPurchaseMessage('Pago completado. El saldo se actualizará en unos segundos.');
      setPurchaseError(null);
      void loadData();
      params.delete('checkout');
      window.history.replaceState({}, '', `${window.location.pathname}?${params.toString()}`.replace(/\?$/, ''));
    }
    if (checkout === 'cancel') {
      setPurchaseMessage('Pago cancelado.');
      setPurchaseError(null);
      params.delete('checkout');
      window.history.replaceState({}, '', `${window.location.pathname}?${params.toString()}`.replace(/\?$/, ''));
    }
  }, []);

  const formatSAC = (amount: number) => {
    return `${amount.toFixed(2)} SAC`;
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'paid': return 'bg-green-100 text-green-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'failed': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const handleInitiatePurchase = async (pkg: SoArtisticCoinPackage) => {
    setPurchasingPackageId(pkg.id);
    setPurchaseMessage(null);
    setPurchaseError(null);
    try {
      const { client_secret } = await adsApiService.createCoinCheckoutSession({
        package_id: pkg.id,
        return_url: `${window.location.origin}?return_to=billing&checkout=success`,
      });
      setCheckoutClientSecret(client_secret);
      setShowCheckoutModal(true);
    } catch (error: any) {
      setPurchaseError(error?.message || 'No se pudo iniciar el pago.');
    } finally {
      setPurchasingPackageId(null);
    }
  };

  const handleViewDetails = (invoice: AdInvoice) => {
    setSelectedInvoice(invoice);
    setShowPurchaseDetailModal(true);
  };

  const handleClosePurchaseDetailModal = () => {
    setShowPurchaseDetailModal(false);
    setSelectedInvoice(null);
  };

  return (
    <div className="p-4 lg:p-6 overflow-x-hidden min-w-0">
      {/* Header */}
      <div className="mb-6 lg:mb-8">
        <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-2">SoArtistic Coins (SAC)</h1>
        <p className="text-gray-600 text-sm lg:text-base">Gestiona tu saldo de SoArtistic Coins y compra paquetes</p>
      </div>

      {purchaseMessage && (
        <div className="mb-6 bg-blue-50 border border-blue-200 text-blue-800 px-4 py-3 rounded-lg text-sm">
          {purchaseMessage}
        </div>
      )}
      {purchaseError && (
        <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
          {purchaseError}
        </div>
      )}

      {/* SAC Information */}
      <div className="bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-xl p-4 lg:p-6 mb-6 lg:mb-8">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-purple-600 rounded-xl">
            <ShoppingBag className="text-white" size={24} />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-bold text-gray-900 mb-2">💎 SoArtistic Coin (SAC)</h3>
            <p className="text-gray-700 text-sm lg:text-base">
              Los <strong>SoArtistic Coins (SAC)</strong> son la moneda virtual para todas tus campañas publicitarias.
              Cada <strong>1 SAC = {formatCurrency(0.10)}</strong>.
            </p>
            <div className="mt-4 bg-white/60 border border-purple-200 rounded-lg p-3 text-sm text-gray-700">
              <p className="font-semibold text-gray-900 mb-1">⏱️ Publicidad por bloques de tiempo</p>
              <p>La publicidad se consume en <strong>bloques de 30 segundos</strong>.</p>
              <p>Cada bloque cuesta <strong>{SAC_PER_30S_BLOCK} SAC</strong> (≈ {formatCurrency(0.90)}).</p>
              <p className="mt-2">Límites por usuario:</p>
              <p>• <strong>{MAX_BLOCKS_PER_24H_PER_USER} bloques cada 24h</strong> (2 horas)</p>
              <p>• <strong>{MAX_CAMPAIGNS_PER_USER} campañas</strong></p>
            </div>
            <div className="flex items-center gap-4 mt-3 text-xs lg:text-sm">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                <span>Valor Fijo</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                <span>Sin Caducidad</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                <span>Uso Exclusivo en Ads</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Current Balance & Spending */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6 mb-6 lg:mb-8">
        <div className="bg-white rounded-xl border border-gray-200 p-4 lg:p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-green-100 rounded-lg">
              <CreditCard className="text-green-600" size={20} />
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="text-xs lg:text-sm font-medium text-gray-600">Saldo de SoArtistic Coins</h3>
              <p className="text-xl lg:text-2xl font-bold text-gray-900">{formatSAC(adAccount?.sac_balance || 0)}</p>
            </div>
          </div>
          <div className="text-xs lg:text-sm text-gray-500">
            Utiliza tus SAC para promocionar tu contenido
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-4 lg:p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Calendar className="text-blue-600" size={20} />
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="text-xs lg:text-sm font-medium text-gray-600">Gasto Este Mes (SAC)</h3>
              <p className="text-xl lg:text-2xl font-bold text-gray-900">{formatSAC(0)}</p>
            </div>
          </div>
          <div className="text-xs lg:text-sm text-gray-500">
            Tu actividad publicitaria en SAC
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-4 lg:p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-purple-100 rounded-lg">
              <RefreshCw className="text-purple-600" size={20} />
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="text-xs lg:text-sm font-medium text-gray-600">Próxima Recarga</h3>
              <p className="text-xl lg:text-2xl font-bold text-gray-900">--</p>
            </div>
          </div>
          <div className="text-xs lg:text-sm text-gray-500">
            Compra más SAC cuando lo necesites
          </div>
        </div>
      </div>

      {/* Token Packages */}
      <div className="bg-white rounded-xl border border-gray-200 mb-6 lg:mb-8 overflow-hidden">
        <div className="px-4 lg:px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg lg:text-xl font-semibold text-gray-900">Comprar SoArtistic Coins</h2>
          <p className="text-gray-600 text-sm lg:text-base mt-1">Elige el paquete que mejor se adapte a tus necesidades y obtén bonificaciones</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 p-4 lg:p-6">
          {tokenPackages.map((pkg) => (
            <div key={pkg.name} className="bg-gray-50 rounded-lg border border-gray-200 p-4 flex flex-col items-center text-center">
              <div className="p-3 bg-blue-100 rounded-full mb-3">
                {pkg.name.includes('Básico') && <ShoppingBag className="text-blue-600" size={24} />}
                {pkg.name.includes('Impulso') && <Zap className="text-blue-600" size={24} />}
                {pkg.name.includes('Creador') && <TrendingUp className="text-blue-600" size={24} />}
                {pkg.name.includes('Agencia') && <Rocket className="text-blue-600" size={24} />}
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-1">{pkg.name}</h3>
              <p className="text-sm text-gray-600 mb-2">{pkg.quantity} SAC</p>
              {pkg.bonus > 0 && (
                <p className="text-xs font-semibold text-green-600 bg-green-100 px-2 py-1 rounded-full mb-3">
                  +{pkg.bonus}% Bonificación ({pkg.totalTokens - pkg.quantity} SAC extra)
                </p>
              )}
              <p className="text-2xl font-bold text-blue-600 mb-4">{formatSAC(pkg.totalTokens)}</p>
              <p className="text-sm text-gray-700 mb-1">Precio: <span className="font-bold">{formatCurrency(pkg.priceEUR)}</span></p>
              <p className="text-xs text-gray-500 mb-4">Impuestos no incluidos (se calculan en el checkout)</p>
              <button
                onClick={() => handleInitiatePurchase(pkg)}
                disabled={purchasingPackageId === pkg.id}
                className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-base font-medium flex items-center justify-center gap-2"
              >
                {purchasingPackageId === pkg.id ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Comprando...
                  </>
                ) : (
                  `Comprar ${pkg.name}`
                )}
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Recent Invoices (now SAC purchase history) */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-4 lg:px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-lg lg:text-xl font-semibold text-gray-900">Historial de Compras de SAC</h2>
            <button className="flex items-center gap-2 px-3 lg:px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm lg:text-base">
              <Download size={16} />
              <span className="hidden lg:inline">Exportar</span>
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left py-3 px-4 lg:px-6 font-medium text-gray-600 text-xs lg:text-sm">Fecha</th>
                <th className="text-left py-3 px-4 lg:px-6 font-medium text-gray-600 text-xs lg:text-sm">Número de Factura</th>
                <th className="text-left py-3 px-4 lg:px-6 font-medium text-gray-600 text-xs lg:text-sm">Tokens Comprados</th>
                <th className="text-left py-3 px-4 lg:px-6 font-medium text-gray-600 text-xs lg:text-sm">Monto Total</th>
                <th className="text-left py-3 px-4 lg:px-6 font-medium text-gray-600 text-xs lg:text-sm">Estado</th>
                <th className="text-left py-3 px-4 lg:px-6 font-medium text-gray-600 text-xs lg:text-sm">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {invoices.length > 0 ? (
                invoices.map((invoice) => (
                  <tr key={invoice.id} className="hover:bg-gray-50">
                    <td className="py-4 px-4 lg:px-6 text-gray-700 text-xs lg:text-sm">
                      {new Date(invoice.created_at).toLocaleDateString('es-ES')}
                    </td>
                    <td className="py-4 px-4 lg:px-6 text-gray-900 text-xs lg:text-sm">
                      {invoice.invoice_number}
                    </td>
                    <td className="py-4 px-4 lg:px-6 font-medium text-gray-900 text-xs lg:text-sm">
                      {formatSAC(invoice.total_spend)}
                    </td>
                    <td className="py-4 px-4 lg:px-6 font-medium text-gray-900 text-xs lg:text-sm">
                      {formatCurrency(invoice.total_amount)}
                    </td>
                    <td className="py-4 px-4 lg:px-6">
                      <span className={`inline-block px-2 py-1 text-xs rounded-full font-medium ${getStatusColor(invoice.status)}`}>
                        {invoice.status === 'paid' ? 'Completado' : invoice.status === 'pending' ? 'Pendiente' : 'Fallido'}
                      </span>
                    </td>
                    <td className="py-4 px-4 lg:px-6">
                      <button
                        onClick={() => handleViewDetails(invoice)}
                        className="text-blue-600 hover:text-blue-700 text-xs lg:text-sm font-medium flex items-center gap-1"
                      >
                        <Eye size={16} />
                        Ver Detalles
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} className="py-12 text-center">
                    <div className="flex flex-col items-center">
                      <ShoppingBag size={48} className="mx-auto text-gray-300 mb-4" />
                      <h3 className="text-lg font-medium text-gray-900 mb-2">No hay historial de compras</h3>
                      <p className="text-gray-500">Tus compras de SoArtistic Coins aparecerán aquí</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Purchase Detail Modal */}
      {showPurchaseDetailModal && selectedInvoice && (
        <PurchaseDetailModal
          invoice={selectedInvoice}
          onClose={handleClosePurchaseDetailModal}
          formatCurrency={formatCurrency}
          formatSAC={formatSAC}
        />
      )}

      {showCheckoutModal && checkoutClientSecret && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl w-full max-w-3xl overflow-hidden">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <div className="font-semibold text-gray-900">Pago seguro</div>
              <button
                type="button"
                onClick={() => {
                  setShowCheckoutModal(false);
                  setCheckoutClientSecret(null);
                }}
                className="p-2 hover:bg-gray-100 rounded-full"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-4">
              <EmbeddedCheckoutProvider
                stripe={stripePromise}
                options={{
                  clientSecret: checkoutClientSecret,
                  onComplete: () => {
                    setShowCheckoutModal(false);
                    setCheckoutClientSecret(null);
                    setPurchaseMessage('Pago completado. El saldo se actualizará en unos segundos.');
                    void loadData();
                  },
                }}
              >
                <EmbeddedCheckout />
              </EmbeddedCheckoutProvider>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BillingView;
